import React, { useCallback, useMemo, useState } from 'react';
import { ActionsBar, Button, Label, Section, Text } from '@ydistri/ds';
import { useGetSkuEntityListsQuery } from '../../apiDetail';
import { EntityListTypeName } from '@ydistri/api-sdk';
import { getEntityListGroups } from './skuEntityListsLib';
import SkuEntityListsGroupTable from './SkuEntityListsGroupTable';
import LoadingIndicator from '../../../../components/global/LoadingIndicator/LoadingIndicator';

interface SkuEntityListsSectionProps {
  skuId: number;
}

/**
 * Section displayed on SKU detail that lists all entity lists the SKU belongs to.
 * Archived lists are hidden by default; the user can toggle their visibility.
 * Entity lists are grouped by type and displayed in a table format.
 * The entity lists of particular type are displayed as expandable rows in a table.
 */
const SkuEntityListsSection: React.FC<SkuEntityListsSectionProps> = ({ skuId }) => {
  const { data: entityLists, isFetching } = useGetSkuEntityListsQuery(skuId);

  // null = user has not toggled yet; derive default from data (show archived only if all are archived)
  const [showArchivedOverride, setShowArchivedOverride] = useState<boolean | null>(null);
  const [activeTypeFilter, setActiveTypeFilter] = useState<EntityListTypeName[]>([]);

  const showArchived = showArchivedOverride ?? entityLists?.every(el => el.isArchived) ?? false;

  const displayedData = useMemo(() => {
    const lists = showArchived ? entityLists : entityLists?.filter(el => !el.isArchived);
    const filteredLists = lists
      ?.slice()
      .sort(
        (a, b) =>
          Number(a.isArchived) - Number(b.isArchived) || (a.name ?? '').localeCompare(b.name ?? ''),
      );

    if (filteredLists) {
      return getEntityListGroups(filteredLists);
    } else {
      return [];
    }
  }, [entityLists, showArchived]);

  const toggleShowArchived = useCallback(() => {
    const nextShowArchived = !(showArchivedOverride ?? showArchived);
    setShowArchivedOverride(nextShowArchived);

    // Reset filters whose type would no longer be visible after the toggle
    if (activeTypeFilter.length > 0) {
      const nextLists = nextShowArchived ? entityLists : entityLists?.filter(el => !el.isArchived);
      const availableTypes = nextLists?.reduce((acc, el) => {
        const types = el.entityListTypes.map(elt => elt.entityListTypeName);
        types.forEach(type => acc.add(type));
        return acc;
      }, new Set<EntityListTypeName>());
      setActiveTypeFilter(prev => prev.filter(t => availableTypes?.has(t)));
    }
  }, [showArchivedOverride, showArchived, activeTypeFilter, entityLists]);

  const archivedCount = entityLists?.filter(el => el.isArchived).length ?? 0;

  const emptyList = !entityLists || entityLists.length === 0;

  const headerActions = useMemo(() => {
    if (archivedCount === 0 || emptyList) return undefined;
    return (
      <ActionsBar>
        <Button $colorVariant="ghost" $sizeVariant="xSmall" onClick={toggleShowArchived}>
          <Text $size="small">
            {showArchived ? 'Hide archived' : `Show ${archivedCount} archived`}
          </Text>
        </Button>
      </ActionsBar>
    );
  }, [archivedCount, emptyList, showArchived, toggleShowArchived]);

  const body = useMemo(() => {
    if (isFetching) {
      return <LoadingIndicator />;
    }

    if (emptyList) {
      return <Label>This Sku is not in any Sku List</Label>;
    }

    return <SkuEntityListsGroupTable data={displayedData} />;
  }, [displayedData, emptyList, isFetching]);

  return (
    <Section header="Sku Lists" headerActions={headerActions}>
      {body}
    </Section>
  );
};

export default SkuEntityListsSection;
